/**
 * Upcoming events without a Lead / Event Rep (owner 2026-11-04, migration 1630). Every event needs a lead rep so its
 * days can be started and closed; this list is where admins and the sales managers fix the ones that don't have one.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getEventsMissingLead } from "../services/eventsMissingLead.service";
import { useAuth } from "../contexts/AuthContext";
import {
  addFieldRepAsLead,
  getEventLeadRepStatus,
  LEAD_STATUS_KEY,
} from "../services/eventLeadRepStatus.service";
import { useToast } from "../components/ui/Toast";

const TYPE_LABEL: Record<string, string> = {
  gun_show: "Gun show",
  range_night: "Range night",
  class: "Class",
  seminar: "Seminar",
  table: "Table",
  speaking_engagement: "Speaking engagement",
};

const whenOf = (iso: string, tz: string): string => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const EventsMissingLead: React.FC = () => {
  const [days, setDays] = useState(60);
  const { user } = useAuth();
  const role = (user?.role ?? "").toLowerCase().replace(/_/g, "");
  const canEdit = ["superadmin", "admin", "staff", "manager"].includes(role);
  const { data, isLoading, error } = useQuery({
    queryKey: ["events-missing-lead", days],
    queryFn: () => getEventsMissingLead(days, 500),
  });
  const rows = data ?? [];
  const total = rows[0]?.total_count ?? 0;
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [adding, setAdding] = useState<string | null>(null);
  // mig 1760: who the Lead / Event Rep field names (often set, just not on the Sales Reps list) + the one-click fix
  const { data: status } = useQuery({
    queryKey: [
      LEAD_STATUS_KEY,
      "missing-lead",
      rows.map((r) => r.event_id).join(","),
    ],
    queryFn: () => getEventLeadRepStatus(rows.map((r) => r.event_id)),
    enabled: rows.length > 0,
    staleTime: 30_000,
  });
  const addLead = async (eventId: string, positionId: string, name: string) => {
    setAdding(eventId);
    try {
      await addFieldRepAsLead(eventId, positionId);
      showToast("success", `${name} is now the lead rep.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["events-missing-lead"] }),
        queryClient.invalidateQueries({ queryKey: [LEAD_STATUS_KEY] }),
        queryClient.invalidateQueries({
          queryKey: ["event-sales-assignments", eventId],
        }),
      ]);
    } catch (e) {
      showToast(
        "error",
        `${name} was not added: ${(e as Error)?.message ?? "unknown error"}`,
      );
    } finally {
      setAdding(null);
    }
  };
  return (
    <div className="space-y-4" data-testid="events-missing-lead">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/events"
            className="text-sm text-primary-600 hover:underline"
          >
            ← Events
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            Events without a lead rep
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Every event needs a Lead / Event Rep so its days can be started and
            closed. Open an event and choose its lead
            {canEdit
              ? " (Edit, or the Sales Reps list)"
              : " on its Sales Reps list"}
            . Events marked No Rep Required / Self-Service are not listed.
          </p>
        </div>
        <label className="text-sm text-gray-700">
          Next{" "}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1"
            aria-label="How far ahead"
          >
            {[7, 14, 30, 60, 90, 180].map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </label>
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-800">
          {(error as Error).message}
        </p>
      )}
      {!isLoading && !error && (
        <p className="text-sm text-gray-700" data-testid="missing-lead-count">
          {total === 0
            ? "Every upcoming event in this window has a lead rep."
            : `${total} upcoming event${total === 1 ? "" : "s"} without a lead rep${
                total > rows.length ? ` (showing the first ${rows.length})` : ""
              }.`}
        </p>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Where</th>
                <th className="px-3 py-2">Why</th>
                <th className="px-3 py-2">Lead / Event Rep field</th>
                <th className="px-3 py-2">Team</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.event_id} data-testid="missing-lead-row">
                  <td className="px-3 py-2">
                    <Link
                      to={`/events/${r.event_id}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {r.title}
                    </Link>
                    <div className="text-xs text-gray-500">
                      {TYPE_LABEL[r.type] ?? r.type}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {whenOf(r.start_time, r.timezone)}
                  </td>
                  <td className="px-3 py-2">
                    {[r.location_city, r.location_state]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {r.why === "no_lead"
                      ? "No lead"
                      : `Lead's seat is vacant or inactive${r.lead_name ? ` (${r.lead_name})` : ""}`}
                  </td>
                  <td className="px-3 py-2" data-testid="missing-lead-field">
                    {(() => {
                      const s = status?.[r.event_id];
                      const name = s?.field_name?.trim();
                      if (!s || !name || s.field_on_list_as === "lead") {
                        return "—";
                      }
                      return (
                        <div className="space-y-1">
                          <div>
                            {name}{" "}
                            <span className="text-xs text-gray-500">
                              (not on the Sales Reps list)
                            </span>
                          </div>
                          {s.can_add_field_lead && s.field_position_id && (
                            <button
                              type="button"
                              className="rounded border border-yellow-400 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-900 hover:bg-yellow-100 disabled:opacity-50"
                              disabled={adding === r.event_id}
                              onClick={() =>
                                void addLead(
                                  r.event_id,
                                  s.field_position_id as string,
                                  name,
                                )
                              }
                              data-testid="missing-lead-add"
                            >
                              {adding === r.event_id
                                ? "Adding…"
                                : `Add ${name} as lead`}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {[
                      r.territory_manager_name &&
                        `TM ${r.territory_manager_name}`,
                      r.regional_manager_name &&
                        `RM ${r.regional_manager_name}`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EventsMissingLead;
